import { createContext } from 'react';

import { AUTH_STATUS } from '../constants';
import type { AuthState, LoginParams, LoginWithSSOParams } from '../hooks/useAuthentication';
import type { AuthLocationState } from '../types';

export interface AuthContextBase {
  login: (loginParams: LoginParams) => Promise<void>;
  authenticate: (options: { loginToken: string; mfaToken?: string }) => Promise<void>;
  logout: (options?: {
    openRoute?: string;
    query?: string;
    redirectBack?: boolean;
    loginState?: AuthLocationState;
  }) => void;
  fetchCurrentUser: (options?: { trackUser?: boolean }) => Promise<void>;
  authState: AuthState;
  isSuperAdmin: boolean;
  loginWithSSO: (loginParams: LoginWithSSOParams) => Promise<void>;
  switchToWorkspace: (workspaceId: string, redirectTo?: string) => Promise<void>;
  deleteUtmParametersCookie: () => void;
  // 3rd party apps keys
  reCaptchaKey: string | null;
  ipInfoToken: string | null;
  // Other flags
  logoutOnUnauthenticated: boolean;
  routes: {
    defaultRedirectToAfterLogin: string;
    login: string;
    userLocked: string;
    premium: string;
    profileSecurity: string;
  };
}

interface AuthContextAnonymous extends AuthContextBase {
  authState: Extract<AuthState, { status: AUTH_STATUS.anonymous }>;
}

interface AuthContextAuthenticated extends AuthContextBase {
  authState: Extract<AuthState, { status: AUTH_STATUS.authenticated }>;
}

export const isAuthenticated = (
  authContext: AuthContextBase,
): authContext is AuthContextAuthenticated =>
  authContext.authState.status === AUTH_STATUS.authenticated;

export const isAnonymous = (authContext: AuthContextBase): authContext is AuthContextAnonymous =>
  authContext.authState.status === AUTH_STATUS.anonymous;

export const AuthContext = createContext<AuthContextBase>({
  authState: { status: AUTH_STATUS.anonymous },
  isSuperAdmin: false,
  reCaptchaKey: null,
  ipInfoToken: null,
  logoutOnUnauthenticated: true,
  routes: {
    defaultRedirectToAfterLogin: '',
    login: '',
    userLocked: '',
    premium: '',
    profileSecurity: '',
  },
  login: () => Promise.resolve(),
  authenticate: () => Promise.resolve(),
  logout: () => undefined,
  fetchCurrentUser: () => Promise.resolve(),
  loginWithSSO: () => Promise.resolve(),
  switchToWorkspace: () => Promise.resolve(),
  deleteUtmParametersCookie: () => undefined,
});
